import { mdiClose } from '@mdi/js';
import Icon from '@mdi/react';
import {
  changePasswordMessages,
  commonButtonMessages,
  getStatusUtenza,
  profilePageMessages,
  timelineProfileMessages,
  useAppDispatch,
  useAppSelector,
  useChangeEmailDialogLogic,
  useChangePassword,
  useChangePhoneNumberDialogLogic,
  useChangeSecondaryPhoneNumberDialogLogic,
  useDashboardPageLogic,
  UserState,
  useSignatureAreaLogic
} from 'azshared';
import React, { FormEvent, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { AppButton, AppButtonType } from '../../../components/buttons/app-button';
import { Card, CardSize } from '../../../components/card/card';
import { AppSuccessDialog } from '../../../components/dialog/app-success-dialog';
import { AppHeader } from '../../../components/header/header';
import { AppInputPassword } from '../../../components/input-text/app-input-password';
import { Loader, LoaderSize } from '../../../components/loader/loader';
import { AppRequiredNote } from '../../../components/required-note/required-note';
import { AppSvgIcon } from '../../../components/svg-icon/app-svg-icon';
import { useGlobalActions } from '../../../hooks/global-actions';
import { ChangeEmailDialogs } from './change-email-dialogs';
import { ChangePhoneNumberDialogs } from './change-phone-number-dialogs';
import { ChangeSecondaryPhoneNumberDialogs } from './change-secondary-phone-number-dialogs';
import './profile-page.scss';

export const ProfilePage = () => {
  const { formatMessage } = useIntl();

  const globalActions = useGlobalActions();
  const dispatch = useAppDispatch();

  const {
    oldPassword,
    newPassword,
    confirmNewPassword,
    changePasswordRequestStatus,
    changePasswordForm,
    openSuccessDialog,
    handleCloseSuccessDialog,
    onChangePasswordForm,
    setConfirmNewPassword,
    setNewPassword,
    setOldPassword,
    handleSubmit
  } = useChangePassword(globalActions);

  const {
    documentiScaduti
  } = useAppSelector((state) => state.data.user);

  const { signature } = useDashboardPageLogic(globalActions);

  useEffect(() => {
    dispatch(getStatusUtenza());
  }, [dispatch]);

  const { goToSignatureAreaFromProfile, updateDocumentsList } = useSignatureAreaLogic(globalActions);

  const { onChangeEmail, changeEmailDisabled } = useChangeEmailDialogLogic(globalActions);
  const { onChangePhoneNumber, changePhoneDisabled } = useChangePhoneNumberDialogLogic(globalActions);
  const { handleEditPhoneNumber, changeSecondaryPhoneDisabled } = useChangeSecondaryPhoneNumberDialogLogic(globalActions);

  const handleKeyDownChangeEmail = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onChangeEmail();
      }
    },
    [onChangeEmail]
  );

  const handleKeyDownChangePhoneNumber = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onChangePhoneNumber();
      }
    },
    [onChangePhoneNumber]
  );

  const handleKeyDownChangePassword = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onChangePasswordForm();
      }
    },
    [onChangePasswordForm]
  );

  const { signFilesEmail, signFilesPhoneNumber, name, surname, userCode, email, phone, secondaryPhone, signFilesRequestStatus, userType, isProspect } =
    useAppSelector<UserState>((state) => state.data.user);

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      handleSubmit();
    },
    [handleSubmit]
  );

  const steps = [formatMessage(timelineProfileMessages.step1), formatMessage(timelineProfileMessages.step2), formatMessage(timelineProfileMessages.step3)];

  // eslint-disable-next-line
  useEffect(() => updateDocumentsList(), []);

  const isPdob = useMemo(() => isProspect || userCode?.length !== 9, [isProspect, userCode]);
  
  const changeEmailEnabled = useMemo(
    () => !isPdob && signature.value === 0 && !signFilesEmail && !changeEmailDisabled,
    [isPdob, changeEmailDisabled, signFilesEmail, signature.value]
  );

  const changePhoneEnabled = useMemo(
    () => !isPdob && signature.value === 0 && !signFilesPhoneNumber && !changePhoneDisabled,
    [isPdob, signFilesPhoneNumber, changePhoneDisabled, signature.value]
  );

  const allDisabled = useMemo(
    () => isPdob || (changeEmailDisabled && changePhoneDisabled && changeSecondaryPhoneDisabled),
    [isPdob, changeEmailDisabled, changePhoneDisabled, changeSecondaryPhoneDisabled]
  );

  return (
    <div className='profile-page'>
      <AppHeader />
      <div className='card-profile'>
        <Card title={formatMessage(profilePageMessages.pageTitle)} back={formatMessage(commonButtonMessages.backLink)} sizeVariant={CardSize.MAXI}>
          {signFilesRequestStatus === 'loading' && (
            <div className='profile-page-loader'>
              <Loader loadersize={LoaderSize.L}></Loader>
            </div>
          )}
          {signFilesRequestStatus !== 'loading' && (
            <>
              <div className='user'>
                <svg className='avatar' />
                <span>
                  {name} {surname}
                </span>
              </div>
              <div className='fields'>
                <div className='field first-column'>
                  <div className='title'>{formatMessage(profilePageMessages.labelIdentificationNumberField)}</div>
                  <div className='description'>{isPdob ? '-' : userCode}</div>
                </div>

                <div className='field first-column'>
                  {/* Change Email */}
                  <div>
                    <div className={`title ${signFilesEmail ? 'red-title' : ''}`}>
                      {formatMessage(signFilesEmail ? profilePageMessages.labelEmailChangedField : profilePageMessages.labelEmailField)}
                      {changeEmailEnabled ? (
                        <span onClick={onChangeEmail} onKeyDown={handleKeyDownChangeEmail} tabIndex={0}>
                          <AppSvgIcon icon='pencil' />
                        </span>
                      ) : (
                        <span>
                          <AppSvgIcon icon='pencil' className={'app-svg-icon-disabled'} />
                        </span>
                      )}
                    </div>
                    <div className='description'>{email}</div>
                  </div>
                  <ChangeEmailDialogs steps={steps} />
                </div>

                <div className='field first-column'>
                  {/* Change Phone Number */}
                  <div>
                    <div className={`title ${signFilesPhoneNumber ? ' red-title' : ''}`}>
                      {formatMessage(signFilesPhoneNumber ? profilePageMessages.labelPhoneNumberChangedField : profilePageMessages.labelPhoneNumberField)}
                      {changePhoneEnabled ? (
                        <span onClick={onChangePhoneNumber} onKeyDown={handleKeyDownChangePhoneNumber} tabIndex={0}>
                          <AppSvgIcon icon='pencil' />
                        </span>
                      ) : (
                        <span>
                          <AppSvgIcon icon='pencil' className={'app-svg-icon-disabled'} />
                        </span>
                      )}
                    </div>
                    <div className='description'>{phone}</div>
                  </div>
                  <ChangePhoneNumberDialogs steps={steps} />
                </div>

                {userType === 'PF' && (
                  <div className='field first-column'>
                    <div className='title'>
                      {formatMessage(profilePageMessages.labelSecondaryPhoneNumberField)}
                      {changeSecondaryPhoneDisabled || isPdob ? (
                        <AppSvgIcon icon='pencil' className={'app-svg-icon-disabled'} />
                      ) : (
                        <AppSvgIcon icon='pencil' onClick={handleEditPhoneNumber} />
                      )}
                    </div>
                    <div className='description'>{secondaryPhone}</div>
                    {!changeSecondaryPhoneDisabled && !isPdob && <ChangeSecondaryPhoneNumberDialogs />}
                  </div>
                )}

                {(signFilesEmail || signFilesPhoneNumber) && (
                  <div className='sign-button'>
                    <AppButton label={formatMessage(profilePageMessages.labelSignUpdateButton)} onClick={goToSignatureAreaFromProfile} />
                  </div>
                )}

                <div className='field'>
                  <div className='title'>
                    {formatMessage(profilePageMessages.labelPasswordField)}
                    {!changePasswordForm && <AppSvgIcon icon='pencil' onClick={onChangePasswordForm} />}
                    {changePasswordForm && (
                      <span onClick={onChangePasswordForm} onKeyDown={handleKeyDownChangePassword} tabIndex={0}>
                        <Icon className='icon' size={1} path={mdiClose} />{' '}
                      </span>
                    )}
                  </div>
                  {!changePasswordForm && <div className='description'>*******************</div>}
                </div>

                {changePasswordForm && (
                  <form className='form-change-password' onSubmit={onSubmit}>
                    <div>
                      <AppInputPassword
                        placeholder={formatMessage(changePasswordMessages.oldPasswordLabel)}
                        label={formatMessage(changePasswordMessages.oldPasswordPlaceholder)}
                        required
                        value={oldPassword}
                        onChange={setOldPassword}
                      />
                      <AppInputPassword
                        placeholder={formatMessage(changePasswordMessages.newPasswordLabel)}
                        label={formatMessage(changePasswordMessages.newPasswordPlaceholder)}
                        required
                        value={newPassword}
                        onChange={setNewPassword}
                      />
                      <AppInputPassword
                        placeholder={formatMessage(changePasswordMessages.confirmPasswordLabel)}
                        label={formatMessage(changePasswordMessages.confirmPasswordPlaceholder)}
                        required
                        value={confirmNewPassword}
                        onChange={setConfirmNewPassword}
                      />
                    </div>

                    <AppRequiredNote />

                    <div className='button confirm-button'>
                      <AppButton
                        type={AppButtonType.SUBMIT}
                        label={formatMessage(changePasswordMessages.changePasswordConfirmButton)}
                        loading={changePasswordRequestStatus === 'loading'}
                      />
                    </div>
                  </form>
                )}
              </div>
            </>
          )}
          {(allDisabled && isPdob) && (
            <div className='disclaimer'>
              <AppSvgIcon icon='alert' />
              <span>{formatMessage(profilePageMessages.disclaimer)}</span>
            </div>
          )}
          {!isPdob && signature.value > 0 && (
            <>
              {!(signFilesEmail || signFilesPhoneNumber) && (
                <div className='disclaimer'>
                  <AppSvgIcon icon='alert' />
                  <span>{formatMessage(profilePageMessages.disclaimer2)}</span>
                </div>
              )}
              {signFilesEmail && (
                <div className='disclaimer'>
                  <AppSvgIcon icon='alert' />
                  <span>{formatMessage(profilePageMessages.disclaimer3)}</span>
                </div>
              )}
              {signFilesPhoneNumber && (
                <div className='disclaimer'>
                  <AppSvgIcon icon='alert' />
                  <span>{formatMessage(profilePageMessages.disclaimer4)}</span>
                </div>
              )}
            </>
          )}
          {documentiScaduti && (
            <div className='disclaimer'>
              <AppSvgIcon icon='alert' />
              <span>{formatMessage(profilePageMessages.disclaimer5)}</span>
            </div>
          )}
        </Card>
      </div>

      {/* Success Dialog Change Password */}
      <AppSuccessDialog open={openSuccessDialog} onClose={handleCloseSuccessDialog}>
        <div className='text-dialog'>{formatMessage(changePasswordMessages.messageChangePasswordSuccess)}</div>
      </AppSuccessDialog>
    </div>
  );
};
